import type { Part } from "@a2a-js/sdk";

import { cancelTask, sendMessageStream } from "../Editor/Copilot/api";
import { extractTaskId } from "../Editor/Copilot/contextId";

export interface PromptOptions {
  userPrompt: string;
  sql: string;
  wrappedText?: { before: string; after: string };
}

// Prompt instruction constants (avoid duplication)

const RULE_NO_PREAMBLE =
  "- Do NOT include any preamble, headings, or extra code fences beyond the single SQL block.";
const RULE_EXPLANATION_ASPECTS =
  "- If an explanation is necessary, place it AFTER the code block as at most 1–2 ultra-brief bullet points covering only key aspects (<= 200 characters total). If obvious, output nothing after the code block.";
const RULE_VALIDATE_QUERY =
  "You must use `tool_query_validate` to validate the generated query BEFORE returning it back to user.";
const FRAGMENT_PREFIX = `You will receive a SQL context split into three parts: BEFORE, SELECTION, AFTER.
- Modify ONLY the SELECTION so that:
  FINAL_SQL = BEFORE + MODIFIED_SELECTION + AFTER
- Do NOT include BEFORE or AFTER in your output.`;

// Let the model decide whether to explain or modify/generate
const RULE_INTENT_DECISION =
  "- If the user's request is explanatory (e.g., 'what is this?'), focus on explaining the provided SQL in Markdown; otherwise, modify/generate SQL per the request.";

// Normalize explanation: collapse 3+ consecutive newlines to 2 and trim
const normalizeExplanationWhitespace = (text: string): string => {
  return (text || "").replace(/\n{3,}/g, "\n\n").trim();
};

const isObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

export const generatePrompt = (options: PromptOptions): string => {
  const hasSql = !!options.sql;

  const instructionParts: string[] = [];
  let body: string;

  if (hasSql) {
    if (options.wrappedText) {
      // Fragment modification mode
      instructionParts.push(FRAGMENT_PREFIX);
      instructionParts.push(
        "Return ONLY the MODIFIED_SELECTION inside ONE markdown code block (use ```sql ... ```)."
      );
      instructionParts.push(
        "- Keep surrounding structure valid; do not add or remove extra semicolons if BEFORE/AFTER already contain them."
      );
      instructionParts.push(RULE_INTENT_DECISION);
      instructionParts.push(RULE_EXPLANATION_ASPECTS);
      instructionParts.push(RULE_NO_PREAMBLE);
      instructionParts.push(RULE_VALIDATE_QUERY);

      body = `User request:\n${options.userPrompt}\n\nBEFORE (read-only):\n\n\`\`\`sql\n${options.wrappedText.before}\n\`\`\`\n\nSELECTION (to modify):\n\n\`\`\`sql\n${options.sql}\n\`\`\`\n\nAFTER (read-only):\n\n\`\`\`sql\n${options.wrappedText.after}\n\`\`\``;
    } else {
      // Full SQL modification mode
      instructionParts.push(
        "Modify the provided SQL according to the user's instruction."
      );
      instructionParts.push(
        "- Return the entire modified SQL inside ONE markdown code block (use ```sql ... ```)."
      );
      instructionParts.push(RULE_INTENT_DECISION);
      instructionParts.push(RULE_EXPLANATION_ASPECTS);
      instructionParts.push(RULE_NO_PREAMBLE);
      instructionParts.push(RULE_VALIDATE_QUERY);

      body = `User request:\n${options.userPrompt}\n\nSQL to modify:\n\n\`\`\`sql\n${options.sql}\n\`\`\``;
    }
  } else {
    // Generation mode
    instructionParts.push(
      "Generate a Firebolt SQL query based on the user's request."
    );
    instructionParts.push(
      "- Return the SQL inside ONE markdown code block (use ```sql ... ```)."
    );
    instructionParts.push(
      "- Always add a closing semicolon to the generated SQL query."
    );
    instructionParts.push(RULE_EXPLANATION_ASPECTS);
    instructionParts.push(RULE_NO_PREAMBLE);
    instructionParts.push(RULE_VALIDATE_QUERY);

    body = `User request:\n${options.userPrompt}`;
  }

  const instruction = instructionParts.join("\n");
  return `${instruction}\n\n${body}`;
};

export const buildFixUserPrompt = (errorMessage: string): string => {
  return [
    "The query below failed with error:",
    "```",
    errorMessage,
    "```",
    "How can I resolve this? Propose a fix.",
  ].join("\n");
};

type LLMResponse = { modifiedSql: string | null; explanation: string };

const extractSqlAndExplanation = (content: string): LLMResponse => {
  const sqlBlockMatch = content.match(/```(?:sql)?\s*([\s\S]*?)\s*```/);
  if (sqlBlockMatch) {
    const modifiedSql = sqlBlockMatch[1];
    const explanation = normalizeExplanationWhitespace(
      content.replace(sqlBlockMatch[0], "").trim()
    );
    return { modifiedSql, explanation };
  }
  return {
    modifiedSql: null,
    explanation: normalizeExplanationWhitespace(content.trim()),
  };
};

export const callA2ALLM = async ({
  prompt,
  signal,
  metadata,
  onStatusUpdate,
}: {
  prompt: string;
  signal: AbortSignal;
  metadata: Record<string, unknown>;
  onStatusUpdate: (status: string) => void;
}): Promise<LLMResponse> => {
  if (signal.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }

  let taskId: string | undefined;
  let aborted = false;

  const execute = async () => {
    const stream = sendMessageStream({
      message: prompt,
      metadata,
    });
    let finalText = "";

    for await (const event of stream) {
      if (aborted || signal.aborted) {
        if (taskId) {
          try {
            await cancelTask(taskId);
          } catch (_err) {
            // ignore
          }
        }
        throw new DOMException("Aborted", "AbortError");
      }

      const eventTaskId = extractTaskId(event);
      // Capture task id as soon as it's available
      if (eventTaskId) {
        taskId = eventTaskId;
        if ((aborted || signal.aborted) && taskId) {
          cancelTask(taskId);
          throw new DOMException("Aborted", "AbortError");
        }
      }

      switch (event.kind) {
        case "status-update": {
          const isFinal = event.final === true;
          const taskState = event.status.state;
          if (isFinal && taskState === "failed") {
            throw new Error(
              "Failed to complete your request. Please try again."
            );
          }

          const parts: Part[] | undefined = event.status.message?.parts;
          if (Array.isArray(parts)) {
            let summaryText: string | null = null;
            for (const part of parts) {
              if (part.kind === "data") {
                const payload = part.data;
                if (isObject(payload)) {
                  const maybeKind = payload.kind;
                  if (
                    typeof maybeKind === "string" &&
                    maybeKind === "io.firebolt.ai.a2a.Notification"
                  ) {
                    const inner = payload.data;
                    if (typeof inner === "string") {
                      summaryText = inner;
                      break;
                    }
                    if (isObject(inner)) {
                      const maybeSummary = inner.summary;
                      if (typeof maybeSummary === "string") {
                        summaryText = maybeSummary;
                        break;
                      }
                    }
                  }
                }
              }
            }
            if (summaryText) {
              onStatusUpdate(summaryText);
              break;
            }
          }
          break;
        }
        case "artifact-update": {
          // Concatenate all text payloads from artifact parts to finalText
          if (event.artifact && Array.isArray(event.artifact.parts)) {
            for (const part of event.artifact.parts) {
              if (part.kind === "text" && typeof part.text === "string") {
                finalText += part.text;
                onStatusUpdate("Finalizing the response...");
              }
            }
          }
          break;
        }
        default: {
          // ignore other events
        }
      }
    }

    return extractSqlAndExplanation(finalText);
  };
  let removeAbortListener: (() => void) | undefined;
  const abortPromise = new Promise<never>((_, reject) => {
    const abortHandler = () => {
      aborted = true;
      if (taskId) {
        cancelTask(taskId);
      }
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal.addEventListener("abort", abortHandler);
    removeAbortListener = () =>
      signal.removeEventListener("abort", abortHandler);
    if (signal.aborted) {
      abortHandler();
    }
  });

  try {
    return await Promise.race([execute(), abortPromise]);
  } finally {
    if (typeof removeAbortListener === "function") {
      removeAbortListener();
    }
  }
};
